import { useMemo } from 'react';
import { ContentNode } from '@amzn/awscat-pattern-library-service-model';

import {
  GuideEditorViewModel,
  useGuideEditorViewModel,
} from '../v2-content-editor/GuideEditorViewModel';
import { filterRenderableSections, hasRenderableRichText } from '../../services/SectionVisibility';

/** Read-only presentation state for the current Guide or Strategy page. */
export interface GuideReadonlyViewModel {
  readonly currentPage: ContentNode;
  readonly nextPage: ContentNode | undefined;
  readonly sections: readonly ContentNode[];
  readonly contentId: string;
  readonly pageTitle: GuideEditorViewModel['pageTitle'];
  readonly isPageBodyVisible: boolean;
}

/** Projects the current page into its non-empty read-only content regions. */
export function useGuideReadonlyViewModel(): GuideReadonlyViewModel | undefined {
  const editorViewModel = useGuideEditorViewModel();
  const sections = useMemo(
    () => filterRenderableSections(editorViewModel?.sections ?? []),
    [editorViewModel?.sections],
  );
  const pageContentData = editorViewModel?.currentPage.contentData;
  const isPageBodyVisible = useMemo(
    () => (pageContentData ? hasRenderableRichText(pageContentData) : false),
    [pageContentData],
  );

  // The underlying editor view model returns undefined during teardown; mirror
  // that here so the view renders null instead of dereferencing undefined.
  if (!editorViewModel) return undefined;

  return {
    currentPage: editorViewModel.currentPage,
    nextPage: editorViewModel.nextPage,
    sections,
    contentId: editorViewModel.contentId,
    pageTitle: editorViewModel.pageTitle,
    isPageBodyVisible,
  };
}
